import { useEffect, useRef, useState, type RefObject } from 'react'

export type UseLockedVerticalCenterPaddingProps = {
  isMobile: boolean
  containerRef: RefObject<HTMLElement | null>
  anchorRef: RefObject<HTMLElement | null>
  contentRef: RefObject<HTMLElement | null>
  lockDelayMs?: number
}

const DEFAULT_LOCK_DELAY_MS = 120

const useLockedVerticalCenterPadding = ({
  isMobile,
  containerRef,
  anchorRef,
  contentRef,
  lockDelayMs = DEFAULT_LOCK_DELAY_MS,
}: UseLockedVerticalCenterPaddingProps) => {
  const hasLockedPaddingRef = useRef(false)
  const [lockedPaddingTop, setLockedPaddingTop] = useState<number | null>(null)

  useEffect(() => {
    if (isMobile) {
      hasLockedPaddingRef.current = false
      setLockedPaddingTop(null)
      return
    }

    const container = containerRef.current
    const anchor = anchorRef.current
    const content = contentRef.current
    if (!container || !anchor || !content) {
      return
    }

    let lockTimeoutId: number | undefined

    const lockPadding = () => {
      if (hasLockedPaddingRef.current) {
        return
      }
      const anchorHeight = anchor.getBoundingClientRect().height
      const contentHeight = content.getBoundingClientRect().height
      if (anchorHeight <= 0 || contentHeight <= 0) {
        return
      }
      setLockedPaddingTop(Math.max((anchorHeight - contentHeight) / 2, 0))
      hasLockedPaddingRef.current = true
    }

    const scheduleLock = () => {
      if (hasLockedPaddingRef.current) {
        return
      }
      if (lockTimeoutId !== undefined) {
        window.clearTimeout(lockTimeoutId)
      }
      lockTimeoutId = window.setTimeout(lockPadding, lockDelayMs)
    }

    scheduleLock()

    const observer = new ResizeObserver(() => {
      scheduleLock()
    })
    observer.observe(container)
    observer.observe(anchor)
    observer.observe(content)

    window.addEventListener('resize', scheduleLock)

    return () => {
      if (lockTimeoutId !== undefined) {
        window.clearTimeout(lockTimeoutId)
      }
      window.removeEventListener('resize', scheduleLock)
      observer.disconnect()
    }
  }, [anchorRef, containerRef, contentRef, isMobile, lockDelayMs])

  return lockedPaddingTop
}

export default useLockedVerticalCenterPadding
